import { AbstractControl } from "@angular/forms";
import { CompareOperator, ComponentType, CustomImageSizeOptions, ResourceType } from "src/app/common/enumerations/enumerations";
import { QuillCustomClasses } from "./quill/quill-custom-image-resize/quill-custom-names";
import { WFSectionLocalResource, WfTaskLocalResource } from 'src/app/components/workflow/workflow';
import { Option, Shielding } from "src/app/components/checklists/checklists";
import { RadMeter } from "src/app/components/catalogs/meters-catalog/rad-meters";
import * as _ from 'lodash';
import { Resource } from "src/app/components/catalogs/beamline-catalog/resource/resources";
import moment from "moment";

import { User } from "src/app/components/catalogs/user-catalog/services/user";
import { WFTableResource } from "src/app/components/workflow/workflow";
import { diff, addedDiff, deletedDiff, updatedDiff, detailedDiff } from 'deep-object-diff';
import { DatePipe } from "@angular/common";
import { EmailAddress } from "src/app/components/olog/olog";

export class utils {

  // #region Enum labels

  static getEnumKeyByEnumValue(enumObj?: Record<string, string | number>, val?: string | number): string | undefined {
    if (!enumObj || val === undefined || val === null) return undefined;

    // For numeric enums, TS adds reverse mappings (0 -> "Key"). For string enums it doesn't.
    // We handle both by searching keys and ignoring numeric reverse keys.
    const keys = Object.keys(enumObj).filter(k => Number.isNaN(Number(k)));
    return keys.find(k => enumObj[k] === val);
  }

  static humanizeEnumKey(key?: string | null): string | undefined {
    if (!key) return undefined;

    // Replace underscores/hyphens with spaces first
    let s = key.replace(/[_-]+/g, " ").trim();

    // Insert spaces:
    // 1) "camelCase" -> "camel Case"
    // 2) "PascalCase" -> "Pascal Case"
    // 3) "HTTPServerError" -> "HTTP Server Error" (acronyms preserved)
    s = s
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")          // aB -> a B
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");      // ABCd -> AB Cd

    // Normalize extra spaces
    s = s.replace(/\s+/g, " ").trim();

    // Optional: capitalize first letter
    // s = s.charAt(0).toUpperCase() + s.slice(1);

    return s;
  }

  /** One-call convenience */
  // UNUSED: No TypeScript or template references were found. Retained for possible future enum-label formatting.
  static getEnumLabelByValue(enumObj?: Record<string, string | number>, val?: string | number): string | undefined {
    const key = this.getEnumKeyByEnumValue(enumObj, val);
    return this.humanizeEnumKey(key);
  }

  // #endregion
  // #region Device detection


  static isMobileDevice() {
    return 'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      window.matchMedia?.('(any-pointer: coarse)').matches;
  }

  // #endregion
  // #region Sorting and object access

  static SortProcedures(a?: string, b?: string): number {
    const aSeparated = a?.match(/[a-zA-Z]+|[0-9]+/g);
    const bSeparated = b?.match(/[a-zA-Z]+|[0-9]+/g);

    for (let i = 0; i < Math.max(aSeparated?.length ?? 0, bSeparated?.length ?? 0); i++) {
      const aElement = aSeparated?.[i];
      const bElement = bSeparated?.[i];

      if (!aElement) {
        return -1;
      }
      if (!bElement) {
        return 1;
      }

      const aNum = Number(aElement);
      const bNum = Number(bElement);

      if (isNaN(aNum) || isNaN(bNum)) {
        // Compare as strings
        if (aElement < bElement) {
          return -1;
        } else if (aElement > bElement) {
          return 1;
        }
      } else {
        // Compare as numbers
        if (aNum < bNum) {
          return -1;
        } else if (aNum > bNum) {
          return 1;
        }
      }
    }
    // All elements are equal up to this point
    return 0;
  }
  static SortBeamlines(
    a?: string | null,
    b?: string | null
  ): number {
    // Null/undefined handling
    if (!a) return b ? 1 : 0;   // a is missing → after b  (or equal)
    if (!b) return -1;          // b is missing → a comes first

    // Normalise
    const A = a.toUpperCase().trim();
    const B = b.toUpperCase().trim();

    // Split into prefix ("BL"/"BR") and version part. Handles all name shapes in use:
    //   "BL 2.0.1" (beamline), "BL02.0_PSS101" (shutter).
    const getParts = (s: string) => {
      const prefix = s.match(/^[A-Z]+/)?.[0] ?? '';            // leading letters, e.g. "BL"/"BR"
      const version = s.slice(prefix.length).match(/\d+(\.\d+)*/)?.[0] ?? ''; // first numeric group, ignores "_PSS101"
      const nums = version ? version.split('.').map(n => parseInt(n, 10)) : []; // numeric chunks
      return { prefix, nums };
    };

    const { prefix: pA, nums: vA } = getParts(A);
    const { prefix: pB, nums: vB } = getParts(B);

    // Prefix comparison (BL < BR)
    if (pA !== pB) return pA < pB ? -1 : 1;

    // Version‑segment comparison
    const len = Math.max(vA.length, vB.length);
    for (let i = 0; i < len; i++) {
      const aSeg = vA[i] ?? 0;   // missing segment → 0
      const bSeg = vB[i] ?? 0;
      if (aSeg !== bSeg) return aSeg < bSeg ? -1 : 1;
    }

    // Identical
    return 0;
  }

  static sortArrayAlphabeticallyWithComplexNumbers(a?: string | null, b?: string | null, dir: string | null = 'asc', ignoreCase: boolean = false) {
    const compareString = (strA?: string | null, strB?: string | null) => {
      if (ignoreCase) {
        strA = strA?.toLowerCase();
        strB = strB?.toLowerCase();
      }

      // Define regular expressions to match alphanumeric and numeric parts
      const regex = /(\D+)|(\d+(\.\d+)?)/g;

      // Extract all alphanumeric and numeric parts from the strings
      const aParts = strA?.match(regex) || [];
      const bParts = strB?.match(regex) || [];

      // Iterate through the parts to compare them
      for (let i = 0; i < Math.min(aParts.length, bParts.length); i++) {
        const partA = aParts[i];
        const partB = bParts[i];

        if (!isNaN(Number(partA)) && !isNaN(Number(partB))) {
          // If both parts are numeric, compare them as numbers
          const numA = parseFloat(partA);
          const numB = parseFloat(partB);

          if (numA < numB) return -1;
          if (numA > numB) return 1;
        } else {
          // Otherwise, compare the parts as strings
          const stringComparison = partA.localeCompare(partB, undefined, { numeric: true });
          if (stringComparison !== 0) {
            return stringComparison;
          }
        }
      }

      // Handle the case where one string is a prefix of the other
      return aParts.length - bParts.length;
    };

    return compareString(dir == 'asc' ? a : b, dir == 'asc' ? b : a);
  };


  static getNestedValue<T>(obj: { [key: string]: any; }, path: string): any {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  }

  static sortByMultipleFields<T extends { [key: string]: any; }>(
    fields: string[]
  ): (a: T, b: T) => number {
    return (a: T, b: T) => {
      for (let field of fields) {
        const aValue = this.getNestedValue(a, field);
        const bValue = this.getNestedValue(b, field);

        if (aValue < bValue) {
          return -1;
        }
        if (aValue > bValue) {
          return 1;
        }
      }
      return 0;
    };
  }

  // #endregion
  // #region Date, cloning, validation, and JSON

  // UNUSED: No TypeScript or template references were found. Retained for possible future relative-date calculations.
  static DatePlusseconds(seconds: number) {
    const d1 = new Date();
    const d2 = new Date(d1);
    d2.setSeconds(d1.getSeconds() + seconds);
    return d2;
  }

  static cloneDeep<T>(data: T): T {
    const copy = _.cloneDeep(data);
    return copy as T;
  }

  static Serialize<T>(data: T): T {
    const result = { ...data };
    for (const key in result) {
      if (Object.prototype.toString.call(data[key]) == "[object Object]") {
        result[key] = null as any;
      }
    }
    return result;
  }

  static IsNullOrWhiteSpace(input: string) {
    return !input || !input?.trim();
  }

  // UNUSED: No TypeScript or template references were found. Retained for possible future reactive-form validation.
  static IsWhiteSpaceReactiveForm(control: AbstractControl) {
    if (typeof control.value === "string") {
      if (!control.value.trim()) {
        return { hasWhiteSpace: true };
      }
    }
    return null;
  }

  static OrderByDate(a: string | number | Date, b: string | number | Date) {
    a = new Date(a);
    b = new Date(b);
    return a > b ? -1 : a < b ? 1 : 0;
  }

  static JSONparse(val?: string | null) {
    if (val)
      try {
        return JSON.parse(val);
      } catch (e) {
        // console.log(e);
        return null;
      }
  }
  static JSONstringify(val: any) {
    const cache = new Set();
    return JSON.stringify(
      val,
      (key, value) => {
        if (typeof value === "object" && value !== null) {
          if (cache.has(value)) {
            // Circular reference found, discard key
            return;
          }
          // Store value in our collection
          cache.add(value);
        }
        return value;
      },
      4
    );
  }

  // #endregion
  // #region Object and collection comparison
  static isEquivalent(x: any, y: any, visited = new Set(), path = ''): boolean {
    if (x === y) return true;

    if (!(x instanceof Object) || !(y instanceof Object)) {
      // console.warn(`Type mismatch at ${path || 'root'}:`, x, y);
      return false;
    }

    if (x.constructor !== y.constructor) {
      // console.warn(`Constructor mismatch at ${path || 'root'}:`, x.constructor.name, y.constructor.name);
      return false;
    }

    if (visited.has(x) || visited.has(y)) {
      // console.warn(`Circular reference detected at ${path || 'root'}`);
      return false;
    }

    visited.add(x);
    visited.add(y);

    const xKeys = Object.keys(x);
    const yKeys = Object.keys(y);

    if (xKeys.length !== yKeys.length) {
      // console.warn(`Key count mismatch at ${path || 'root'}:`, xKeys.length, yKeys.length);
      return false;
    }

    for (const key of xKeys) {
      const fullPath = path ? `${path}.${key}` : key;

      if (!y.hasOwnProperty(key)) {
        // console.warn(`Missing key '${key}' in second object at ${fullPath}`);
        return false;
      }

      const xVal = x[key];
      const yVal = y[key];

      if (typeof xVal === 'object' && xVal !== null) {
        if (!this.isEquivalent(xVal, yVal, visited, fullPath)) return false;
      } else {
        if (xVal !== yVal) {
          // console.warn(`Value mismatch at ${fullPath}:`, xVal, yVal);
          return false;
        }
      }
    }

    return true;
  }



  static isNull(x: any, res: any) {
    if (x == undefined || x == null) {
      return res;
    } else {
      return x;
    }
  }

  /* finds the intersection of
   * two arrays in a simple fashion.
   *
   * PARAMS
   *  a - first array, must already be sorted
   *  b - second array, must already be sorted
   *
   * NOTES
   *
   *  Should have O(n) operations, where n is
   *    n = MIN(a.length(), b.length())
   */
  static intersect(a: Iterable<unknown> | null | undefined, b: Iterable<unknown> | null | undefined) {
    const setB = new Set(b);
    const intersect = [...new Set(a)].filter((x) => setB.has(x));
    return intersect.length > 0;
  }

  static compare(
    a?: number | string | Date | null,
    b?: number | string | Date | null,
    isAsc?: boolean
  ) {
    if (a && b)
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    return 0;
  }
  static sort(
    a: number | string | Date | boolean | null | undefined,
    b: number | string | Date | boolean | null | undefined,
    isAsc: boolean = true
  ) {
    if (a === undefined || a === null) return isAsc ? 1 : -1;
    if (b === undefined || b === null) return isAsc ? -1 : 1;

    if (typeof a === 'string' && typeof b === 'string') {
      return a.localeCompare(b) * (isAsc ? 1 : -1);
    }

    if (moment.isDate(a) && moment.isDate(b)) {
      const momentA = moment(a);
      const momentB = moment(b);
      return momentA.diff(momentB) * (isAsc ? 1 : -1);
    }

    // For numbers, booleans, and other types
    return (((a as number) ?? 0) - ((b as number) ?? 0)) * (isAsc ? 1 : -1);
  }

  // #endregion
  // #region String and enum conversion

  static isNullValue(value?: string | null) {
    return value == null || value == "" || value == undefined;
  }

  static replaceAll(str: string, find: string | RegExp, replace: any) {
    return str?.replace(new RegExp(find, "g"), replace);
  }

  static getENUM(ENUM: any): any[] {
    const myEnum: { key: string; value: any; }[] = [];
    const objectEnum = Object.entries(ENUM);
    objectEnum.map((e) => {
      if (typeof e[1] === "number") {
        myEnum.push({ key: e[0], value: e[1] });
      }
    });
    return myEnum.filter(x => !isNaN(x.value));
  }

  static getENUMString(ENUM: any): any[] {
    const myEnum: any[] = [];
    const objectEnum = Object.entries(ENUM);
    objectEnum.map((e) => {
      myEnum.push({ key: e[0], value: e[1] });
    });
    return myEnum;
  }

  static enumToIdNameArray(enumObj: any): { id: number | string; name: string; }[] {
    return Object.keys(enumObj)
      .filter(key => isNaN(Number(key))) // Exclude numeric keys
      .map(key => ({
        id: enumObj[key], // Enum value
        name: key // Enum key
      }));
  }

  // #endregion
  // #region Image editor and HTML cleanup

  // Resize images inside quill editor
  static resizeImage(customSize: any, event: any) {
    let naturalWidth;
    let normalWidth = 504;
    let smallDivider = 2;
    let fitDivider = 1.5;

    if (screen.width < 1000) {
      normalWidth = screen.width;
      smallDivider = 4;
      fitDivider = 2.5;
    }

    switch (customSize) {
      case CustomImageSizeOptions.Small:
        naturalWidth = normalWidth / smallDivider;
        break;
      case CustomImageSizeOptions.Fit:
        naturalWidth =
          event.target.naturalWidth > normalWidth
            ? normalWidth / fitDivider
            : event.target.naturalWidth / fitDivider;
        break;
      case CustomImageSizeOptions.Original:
        naturalWidth = event.target.naturalWidth;
        break;
    }
    event.target.width = naturalWidth;
  }

  // Removes containers for the custom image resize inside quill editor
  static removeExistingCustomContainers() {
    const html = '<p>Small | Fit | Original</p>';
    const elements = document.getElementsByClassName(
      QuillCustomClasses.custom_image_sizes_class
    );
    while (elements.length > 0) {
      const parent = elements[0].parentNode?.parentElement;
      elements[0].parentNode?.removeChild(elements[0]);
      if (parent)
        this.removeHtmlInstancesFromElement(parent, html);
    }

    this.removeHtmlInstances(html);
  }

  static removeHtmlInstancesFromElement(element: HTMLElement | null, html: string) {
    const regex = new RegExp(html.replace(/\|/g, '\\|'), 'g'); // Escape the pipe character in the regex
    if (element)
      element.innerHTML = element.innerHTML.replace(regex, '');
  }

  static removeHtmlInstances(html: string) {
    const quillEditors = document.getElementsByClassName('ql-editor');

    Array.from(quillEditors).map((editor) => {
      const regex = new RegExp(html.replace(/\|/g, '\\|'), 'g'); // Escape the pipe character in the regex
      editor.innerHTML = editor.innerHTML.replace(regex, '');
    });
  }

  // #endregion
  // #region Domain resource conversion

  static convertToResource(object: any): Resource {
    const resource = new Resource();
    const objectProps = Object.entries(object);
    const resourceProps = Object.entries(resource);

    objectProps.map((p) => {
      const propName = p[0];
      const propValue = p[1];
      const resourceProp = resourceProps.find((x) => x[0].toLowerCase() === propName.toLowerCase());

      if (resourceProp) {
        // Use type assertion to access properties dynamically
        (resource as any)[resourceProp[0]] = propValue;
      }
    });

    return resource;
  }


  static convertObjectKeysToCamelCase<T>(obj: any | any[]): T | T[] {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.convertObjectKeysToCamelCase(item)) as T[];
    }

    const instance = {} as { [key: string]: any; };

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const camelCaseKey = key.charAt(0).toLowerCase() + key.slice(1);
        instance[camelCaseKey] = this.convertObjectKeysToCamelCase(obj[key]);
      }
    }

    return instance as T;
  }
  // #endregion
  // #region General validation and formatting

  static isValidDate(value: any): boolean {
    if (value instanceof Date) {
      // If the value is already a Date object, it's considered a valid date.
      return !isNaN(value.getTime());
    } else if (typeof value === 'string') {
      // If the value is a string, attempt to parse it as a date.
      const date = new Date(value);
      return !isNaN(date.getTime());
    } else {
      // If the value is neither a Date object nor a string, it's not a valid date.
      return false;
    }
  }


  static cleanHTML(text: string): string {
    return text ? text.replace(/(<([^>]+)>)/gi, "") : "";
  }
  static roundNumber(num: string | number, scale: number): number {
    const numStr = "" + num; // Convert the input to a string

    if (!numStr.includes("e")) {
      // Handle numbers without exponential notation
      return +(Math.round(+(num + "e+" + scale)) + "e-" + scale);
    } else {
      // Handle numbers in exponential notation
      const [base, exponent] = numStr.split("e").map(str => +str); // Split the base and exponent
      const newExponent = +exponent + scale; // Adjust the exponent

      return +(Math.round(+(base + "e" + newExponent)) + "e-" + scale); // Round and adjust the exponent back
    }
  }

  static insertArrayAt(array: any, index: any, arrayToInsert: any) {
    array.splice(index, 0, ...arrayToInsert);
    return array;
  }

  static numberWithCommas(x: { toString: () => string; }) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  static pushNoRepeat(array: any[], item: any) {
    if (!array.includes(item)) {
      array.push(item);
    }
    return array;
  }

  static compareNumbers(n1: number, n2: number, op: CompareOperator) {
    switch (op) {
      case CompareOperator["="]:
        return n1 == n2;
      case CompareOperator["<"]:
        return n1 < n2;
      case CompareOperator["<="]:
        return n1 <= n2;
      case CompareOperator[">"]:
        return n1 > n2;
      case CompareOperator[">="]:
        return n1 >= n2;
      case CompareOperator["<>"]:
        return n1 != n2;
      default:
        return false;
    }
  }

  // #endregion
  // #region Workflow task state

  static taskComplete(task: WfTaskLocalResource) {
    switch (task.component?.type) {
      case 0: // Signature
        if (task.componentID != ComponentType.CloseButton) {
          task.isCompleted = task.wfTasks?.[0]?.signedBy != null;
        } else {
          task.isCompleted = true;
        }
        break;

      case 1: // Normal
        task.isCompleted = this.checkCompletedByComponent(task);
        break;

      default:
        task.isCompleted = true;
    }
    return task.isCompleted;
  }

  static checkCompletedByComponent(task: WfTaskLocalResource) {
    let valueObjects = null;
    switch (task.componentID) {
      case ComponentType.CheckboxMultiple:
      case ComponentType.CheckboxMultipleWithTextbox:
        valueObjects = this.JSONparse(this.getTextValue(task.text));
        const allRequired = this.JSONparse(task.configuration)?.required;
        if (valueObjects) {
          const options = valueObjects?.find((x: { key: string; }) => x.key == "checked")
            ?.val as Option[];
          if (allRequired) {
            return options.find(x => !x.checked) == null;
          }
          else {
            return options.find((x) => x.checked) != null;
          }
        }
        return false;
      case ComponentType.MultipleTextbox:
        valueObjects = this.JSONparse(this.getTextValue(task.text));
        if (valueObjects) {
          const options = valueObjects?.find((x: { key: string; }) => x.key == "values")
            ?.val as Option[];
          return options.find((x) => !x.text) == null;
        }
        break;
      case ComponentType.CheckboxMultipleSign:
        const options = this.JSONparse(this.getTextValue(task.text));
        return options ? options.find((x: { userID: null; }) => x.userID == null) == null : false;
      case ComponentType.RadMeters:
        valueObjects = this.JSONparse(this.getTextValue(task.text));
        if (valueObjects && valueObjects.data?.length) {
          const radMeters = valueObjects.data as RadMeter[];
          return !radMeters.some(r => !r.backgroundReadingChangedByID && !r.sourceReadingChangedByID);
        }
        break;
      case ComponentType.ShieldingsComponent:
        const shieldings = this.JSONparse(this.getTextValue(task.text)) as Shielding[];
        const unchecked = shieldings?.filter(s => s.resourceID).map(s => s.roleOptions).filter(o => o?.some((r) => !r.checked));
        return unchecked?.length == 0;
      case ComponentType.Catl:
      case ComponentType.Olog:
      case ComponentType.StatusChange:
        return true;
      default:
        // Completion follows the latest workflow execution when a task has been repeated.
        return task.wfTasks?.at(-1)?.signedBy != null;
    }
    return false;
  }

  static getTextValue(text: any) {
    const storedValues = this.JSONparse(text);
    if (
      storedValues &&
      storedValues[0]?.Name == null &&
      storedValues[0]?.stringValue
    ) {
      return storedValues[0].stringValue;
    }
    return text;
  }

  // #endregion
  // #region Strings, arrays, dates, and email

  static nameToCamelCase(input: string): string {
    const words = input.split(' ');
    const camelCaseWords = words.map(word => {
      if (word.length > 0) {
        return word[0].toUpperCase() + word.slice(1).toLowerCase();
      }
      return '';
    });
    return camelCaseWords.join(' ');
  }

  static compareArrays(arr1: any[], arr2: any[]): boolean {
    if (arr1.length !== arr2.length) {
      return false; // If the arrays have different lengths, they can't be equal.
    }

    for (let i = 0; i < arr1.length; i++) {
      // Use a deep equality comparison (e.g., using JSON.stringify) to compare objects.
      if (JSON.stringify(arr1[i]) !== JSON.stringify(arr2[i])) {
        return false; // If any elements differ, the arrays are not equal.
      }
    }

    return true; // If no differences were found, the arrays are equal.
  }

  static stripHTML(html: any) {
    // Replace &nbsp; with a space, then strip HTML tags
    return html.replace(/&nbsp;/g, ' ').replace(/<\/?[^>]+(>|$)/g, "");
  }


  static getRandomInt(min: number, max: number): number {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  static stripSpaces(text?: string | null) {
    if (text)
      return text.replaceAll(' ', '');
    return null;
  }

  static getAllRequesters(wfTable?: WFTableResource): User[] {
    return wfTable?.wfTableLocal?.wfSectionLocals.flatMap((section: WFSectionLocalResource) => section.wfSignatures?.filter(s => s.requesterSignature)?.flatMap((signature) => signature.users.flatMap(user => user) || []) || []) || [];
  }

  static isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  static formatDate(date: Date | undefined | string | null): string | null {
    const datePipe = new DatePipe('en-US');
    return datePipe.transform(date, 'MM-dd-yyyy');
  }

  static formatDatePipe(date: Date | string | null | undefined, format: string): string {
    const datePipe = new DatePipe('en-US');
    return datePipe.transform(date, format) || '';
  }

  static parseEmailAddresses(emailStrings?: string[]) {
    const result = emailStrings?.filter(e => e != undefined).map((emailString) => {
      // Match strings in the format: "Name <email@domain.com>"
      const emailRegex = /^(.*?)[\s]*<([\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,})>$/;
      const match = emailString?.match(emailRegex);

      if (match) {
        const name = match[1]?.trim();
        const address = match[2]?.trim();
        return {
          name: name || '', // Use empty string if no name is found
          address,
          isCustom: false,  // Default value, adjust as needed
        } as EmailAddress;
      }
      return null;
    }).filter((email) => email !== null);
    return result as EmailAddress[];
  }

  static getDifferences<T extends object>(obj1: T, obj2: T): Partial<T> {

    function isObject(value: any): value is object {
      return value !== null && typeof value === 'object' && !Array.isArray(value);
    }

    const differences: Partial<T> = {};

    for (const key in obj1) {
      if (!Object.prototype.hasOwnProperty.call(obj1, key)) continue;

      const val1 = obj1[key];
      const val2 = obj2[key];

      const areObjects = isObject(val1) && isObject(val2);

      if (areObjects && val1 && val2) {
        const nestedDiff = this.getDifferences(val1 as object, val2 as object);
        if (Object.keys(nestedDiff).length > 0) {
          differences[key] = nestedDiff as any;
        }
      } else if (val1 !== val2) {
        differences[key] = val2;
      }
    }

    return differences;
  }



  static formatBytes(bytes: number): string {
    if (bytes === 0) return '0 bytes';

    const k = 1024;
    const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);

    // Show up to 2 decimals for KB and larger
    return `${value.toFixed(i === 0 ? 0 : 2)} ${sizes[i]}`;
  }


  static mergeUnique<T>(existing: T[], incoming: T[], key: keyof T): T[] {
    return [
      ...existing,
      ...incoming.filter(item => !existing.some(e => e[key] === item[key]))
    ];
  }

  // #endregion

}

//

// ---- Types for array filtering/selection ----
export type ArrayCompareFilter<T = any> =
  | ((item: T) => boolean) // predicate
  | string                 // key name, e.g., 'id'
  | {
    predicate?: (item: T) => boolean;
    by?: keyof T | ((item: T) => unknown);  // compare by this key / function
    select?: (keyof T)[];                   // project object to these fields before compare
    orderInsensitive?: boolean;             // default: true
  };

// ---- Utilities ----
function project<T extends Record<string, unknown>>(x: T, keys: (keyof T)[]) {
  const o: Partial<T> = {};
  for (const k of keys) o[k] = x[k];
  return o;
}

function normalizeArrayForCompare(
  arr: any[],
  filter?: ArrayCompareFilter,
): any[] {
  let out = arr ?? [];

  // 1) Apply predicate (function or in options)
  if (typeof filter === 'function') {
    out = out.filter(filter as (x: any) => boolean);
  } else if (filter && typeof filter === 'object' && !Array.isArray(filter)) {
    if (filter.predicate) out = out.filter(filter.predicate);
  }

  // 2) Optional projection (select a subset of fields to compare)
  if (filter && typeof filter === 'object' && !Array.isArray(filter) && filter.select) {
    out = out.map(x => (x && typeof x === 'object') ? project(x, filter.select as any) : x);
  }

  // 3) Key selector: map each item to a comparable value
  if (typeof filter === 'string') {
    const key = filter;
    out = out.map(x => (x && typeof x === 'object') ? (x as any)[key] : x);
  } else if (filter && typeof filter === 'object' && filter.by) {
    out = out.map(x =>
      typeof filter.by === 'function' ? filter.by(x) : (x && typeof x === 'object') ? (x as any)[filter.by as any] : x
    );
  } else {
    // default: if item has id/ID, compare by that to stabilize objects
    out = out.map(x => (x && typeof x === 'object')
      ? ((x as any).id ?? (x as any).ID ?? x)
      : x
    );
  }

  // 4) Order handling (default order-insensitive)
  const orderInsensitive = typeof filter === 'object' && filter !== null && 'orderInsensitive' in filter
    ? !!filter.orderInsensitive
    : true;

  if (orderInsensitive) {
    return [...out].sort((a, b) => String(a).localeCompare(String(b)));
  }
  return out;
}

export function removeCircularReferences<T>(obj: T): T {
  const seen = new WeakSet();

  return JSON.parse(
    JSON.stringify(obj, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          // drop circular reference
          return undefined;
        }
        seen.add(value);
      }
      return value;
    })
  );
}


function shallowEqualArrays(a: unknown[], b: unknown[]) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function isArray(v: unknown): v is unknown[] { return Array.isArray(v); }
function isDate(v: unknown): v is Date { return v instanceof Date; }
function isPlainObject(v: unknown): v is Record<string, unknown> {
  return Object.prototype.toString.call(v) === '[object Object]';
}

// ---- Compare by kind with optional array filter ----
export function compareByKind(a: unknown, b: unknown, arrayFilter?: ArrayCompareFilter): boolean {
  // if different kinds, it's a diff
  const ka = kind(a), kb = kind(b);
  if (ka !== kb) return true;

  switch (ka) {
    case 'null':
    case 'undefined':
    case 'scalar':
      return a !== b;

    case 'date':
      return new Date(a as any).getTime() !== new Date(b as any).getTime();

    case 'array': {
      const na = normalizeArrayForCompare(a as unknown[], arrayFilter);
      const nb = normalizeArrayForCompare(b as unknown[], arrayFilter);
      return !shallowEqualArrays(na, nb);
    }

    case 'object':
      // simple deep compare; swap for your existing normalize+JSON compare if preferred
      try { return JSON.stringify(a) !== JSON.stringify(b); }
      catch { return a !== b; }
  }
}

type ValueKind = 'undefined' | 'null' | 'scalar' | 'date' | 'array' | 'object';
function kind(v: unknown): ValueKind {
  if (v === undefined) return 'undefined';
  if (v === null) return 'null';
  if (isDate(v)) return 'date';
  if (isArray(v)) return 'array';
  if (isPlainObject(v)) return 'object';
  return 'scalar';
}

// export const compositeKey = (id: unknown, type: unknown) => `${id}|${type}`;
type Primitive = string | number | boolean | bigint | symbol | null | undefined;

interface CompositeKeyOptions {
  sep?: string;          // separator between parts (default: '|')
  nullToken?: string;    // token for null/undefined (default: '')
  sortKeys?: boolean;    // sort object keys for stability (default: true)
}

export function compositeKey<T extends readonly unknown[]>(
  ...parts: [...T]
): string;
export function compositeKey(
  options: CompositeKeyOptions,
  ...parts: unknown[]
): string;
export function compositeKey(...args: unknown[]): string {
  const hasOpts = typeof args[0] === 'object' && !Array.isArray(args[0]) && args[0] !== null
    && ('sep' in (args[0] as any) || 'nullToken' in (args[0] as any) || 'sortKeys' in (args[0] as any));
  const opts: CompositeKeyOptions = hasOpts ? (args.shift() as CompositeKeyOptions) : {};
  const sep = opts.sep ?? '|';
  const nullToken = opts.nullToken ?? '';
  const sortKeys = opts.sortKeys ?? true;

  const toStable = (v: unknown): string => {
    if (v === null || v === undefined) return nullToken;
    if (v instanceof Date) return v.toISOString();
    const t = typeof v;
    if (t === 'string' || t === 'number' || t === 'boolean' || t === 'bigint' || t === 'symbol') {
      return String(v);
    }
    if (Array.isArray(v)) {
      return '[' + v.map(toStable).join(',') + ']';
    }
    if (t === 'object') {
      const obj = v as Record<string, unknown>;
      const keys = Object.keys(obj);
      if (sortKeys) keys.sort();
      const body = keys.map(k => `${k}:${toStable(obj[k])}`).join(',');
      return '{' + body + '}';
    }
    // functions/others: fall back to String
    return String(v);
  };

  return (args as unknown[]).map(toStable).join(sep);
}


export function getBooleanValue(text?: string | null): boolean {
  if (!text)
    return false;

  const storedValues = utils.JSONparse(text);
  if (
    storedValues &&
    storedValues[0]?.Name == null &&
    storedValues[0]?.stringValue
  ) {
    const values = utils.JSONparse(storedValues[0].stringValue);
    if (Array.isArray(values)) {
      return values?.find((x) => x.key == "checkedYes")?.val;
    }
  }
  return false;
}

type Scalar = string | number | boolean | null | undefined | Date;

function isScalar(value: any): value is Scalar {
  return (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value instanceof Date
  );
}

function areDatesEqual(a: any, b: any): boolean {
  return a instanceof Date &&
    b instanceof Date &&
    a.getTime() === b.getTime();
}

export function detectChanges(
  newObj: any,
  oldObj: any,
  path: string = '',
  visited = new WeakMap<object, WeakSet<object>>()
): string[] {
  const changes: string[] = [];

  // same reference
  if (newObj === oldObj) return changes;

  // Date handling
  if (newObj instanceof Date && oldObj instanceof Date) {
    if (newObj.getTime() !== oldObj.getTime()) {
      changes.push(path);
    }
    return changes;
  }

  // scalar handling
  if (isScalar(newObj) || isScalar(oldObj)) {
    if (newObj !== oldObj && !areDatesEqual(newObj, oldObj)) {
      changes.push(path);
    }
    return changes;
  }

  // array handling
  if (Array.isArray(newObj) || Array.isArray(oldObj)) {
    // ignore arrays completely if you only want scalar properties
    return changes;

    // or compare array items if needed:
    // if (!Array.isArray(newObj) || !Array.isArray(oldObj)) {
    //   changes.push(path);
    //   return changes;
    // }
    // const maxLength = Math.max(newObj.length, oldObj.length);
    // for (let i = 0; i < maxLength; i++) {
    //   changes.push(...detectChanges(newObj[i], oldObj[i], `${path}[${i}]`, visited));
    // }
    // return changes;
  }

  // only objects remain here
  if (typeof newObj !== 'object' || typeof oldObj !== 'object' || !newObj || !oldObj) {
    if (newObj !== oldObj) {
      changes.push(path);
    }
    return changes;
  }

  // circular reference protection
  let seenOldSet = visited.get(newObj);
  if (!seenOldSet) {
    seenOldSet = new WeakSet<object>();
    visited.set(newObj, seenOldSet);
  } else if (seenOldSet.has(oldObj)) {
    return changes;
  }
  seenOldSet.add(oldObj);

  const keys = new Set([
    ...Object.keys(newObj),
    ...Object.keys(oldObj)
  ]);

  for (const key of keys) {
    const newValue = newObj[key];
    const oldValue = oldObj[key];
    const currentPath = path ? `${path}.${key}` : key;

    changes.push(
      ...detectChanges(newValue, oldValue, currentPath, visited)
    );
  }

  return changes;
}
